/**
 * Realtor-specific email trigger configurations
 * This file contains email configurations for Brokerage license type triggers
 * that should be merged with the main emailConfig.ts
 *
 * Branding in the email shell (logo + social links + customer message) uses the same rule as
 * all triggers: `sendTriggerEmails` loads `GET .../public/email-templates/users/:id` via
 * `fetchPublicEmailTemplateByUserId` (re-exported from `emailConfig.ts`; see `emailService.ts`).
 */

export const realtorEmailTriggers = {
  "Brokerage Agent license activated": {
    subject: {
      BROKERAGE_FIRM: "Agent License Activated",
      BROKERAGE_TEAM_MEMBER: "Agent License Activated",
      AGENT: "Your Realesy Agent License Is Now Active",
    },
    title: "Agent License Activated",
    logoUrl: "", // Logo is imported directly in emailTemplate.ts
    headerBgColor: "#ffffff",
    bodyLines: [], // Using bodyHtml instead
    bodyHtml: {
      BROKERAGE_FIRM: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          An agent under your brokerage has just activated their Realesy license.
        </p>
        
        <h3 class="email-heading-h3">
          Agent Details
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Name</td>
              <td class="email-order-details-value-bold-first">{{agentName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Email</td>
              <td class="email-order-details-value-link">{{agentEmail}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Activated On</td>
              <td class="email-order-details-value-bold">{{activatedOn}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Office</td>
              <td class="email-order-details-value-bold">{{officeName}}</td>
            </tr>
          </table>
        </div>
        
        <p class="email-paragraph-small">
          You can now view their activity and assist them as needed via your dashboard.
        </p>
      `,
      BROKERAGE_TEAM_MEMBER: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          An agent under your brokerage has just activated their Realesy license.
        </p>
        
        <h3 class="email-heading-h3">
          Agent Details
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Name</td>
              <td class="email-order-details-value-bold-first">{{agentName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Email</td>
              <td class="email-order-details-value-link">{{agentEmail}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Activated On</td>
              <td class="email-order-details-value-bold">{{activatedOn}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Office</td>
              <td class="email-order-details-value-bold">{{officeName}}</td>
            </tr>
          </table>
        </div>
        
        <p class="email-paragraph-small">
          You can now view their activity and assist them as needed via your dashboard.
        </p>
      `,
      AGENT: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          Your Realesy Agent license is now active! You can start placing sign requests, tracking installations, and managing your listing assets right away.
        </p>
        
        <h3 class="email-heading-h3">
           Account Details
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Agent Name</td>
              <td class="email-order-details-value-bold-first">{{agentName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Brokerage</td>
              <td class="email-order-details-value-bold">{{brokerageName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Login URL</td>
              <td class="email-order-details-value-link">{{loginUrl}}</td>
            </tr>
          </table>
        </div>
        
        <p class="email-paragraph-small">
          Need help getting started? Visit your dashboard or reach out to your brokerage office for support.
        </p>
      `,
    },
    cta: {
      BROKERAGE_FIRM: { text: " View Agent Details", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      BROKERAGE_TEAM_MEMBER: { text: " View Agent Details", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      AGENT: { text: " Go to Dashboard", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
    },
    footer: "RealEsy. All rights reserved."
  },
  "Brokerage Sales order": {
    subject: {
      BROKERAGE_FIRM: " Sales Order Placed – #{{orderNumber}}",
      BROKERAGE_TEAM_MEMBER: " Sales Order Placed – #{{orderNumber}}",
      SIGNAGE_FIRM: " New Signage Order Assigned – #{{orderNumber}}",
      AGENT: "️ Signage Order Created for You – #{{orderNumber}}",
    },
    title: "Sales Order Created",
    logoUrl: "", // Logo is imported directly in emailTemplate.ts
    headerBgColor: "#ffffff",
    bodyLines: [], // Using bodyHtml instead
    bodyHtml: {
      BROKERAGE_FIRM: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          A new sales order has been successfully placed under {{brokerageFirmName}}. Below are the details:
        </p>
        
        <h3 class="email-heading-h3">
           Order Details
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">#{{orderNumber}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Order Date</td>
              <td class="email-order-details-value-bold">{{orderDate}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Placed By</td>
              <td class="email-order-details-value-bold">{{placedBy}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Assigned Supplier</td>
              <td class="email-order-details-value-bold">{{signageCompanyName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Drop-Off Location</td>
              <td class="email-order-details-value-bold">{{dropOffLocation}}</td>
            </tr>
          </table>
        </div>
        
        {{lineItems}}
        
        <p class="email-paragraph-small">
          You can view the full order and track its status from your dashboard.
        </p>
      `,
      BROKERAGE_TEAM_MEMBER: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          A new sales order has been successfully placed under {{brokerageFirmName}}. Below are the details:
        </p>
        
        <h3 class="email-heading-h3">
           Order Details
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">#{{orderNumber}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Order Date</td>
              <td class="email-order-details-value-bold">{{orderDate}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Placed By</td>
              <td class="email-order-details-value-bold">{{placedBy}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Assigned Supplier</td>
              <td class="email-order-details-value-bold">{{signageCompanyName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Drop-Off Location</td>
              <td class="email-order-details-value-bold">{{dropOffLocation}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Products</td>
              <td class="email-order-details-value-bold">{{productsOrdered}}</td>
            </tr>
          </table>
        </div>
        
        <p class="email-paragraph-small">
          You can view the full order and track its status from your dashboard.
        </p>
      `,
      SIGNAGE_FIRM: `
        <h2 class="email-heading-h2">
          Hi {{supplierName}},
        </h2>
        <p class="email-paragraph">
          A new signage order has been assigned to you from {{brokerageFirmName}}. Please find the order details below:
        </p>
        
        <h3 class="email-heading-h3">
           Order Details
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">#{{orderNumber}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Order Date</td>
              <td class="email-order-details-value-bold">{{orderDate}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Submitted By</td>
              <td class="email-order-details-value-bold">{{submittedBy}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Delivery Location</td>
              <td class="email-order-details-value-bold">{{deliveryLocation}}</td>
            </tr>
          </table>
        </div>
        
        {{lineItems}}
        
        <p class="email-paragraph-small">
          Please update the production and installation status in Realesy once the order is in progress.
        </p>
      `,
      AGENT: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          {{signageCompanyName}} has created a signage order on your behalf following your request via {{orderMethod}}.
        </p>
        
        <h3 class="email-heading-h3">
           Order Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">#{{orderNumber}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value-bold">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Scheduled By</td>
              <td class="email-order-details-value-bold">{{scheduledBy}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Requested Date</td>
              <td class="email-order-details-value-bold">{{requestedDate}}</td>
            </tr>
          </table>
        </div>
        
        {{lineItems}}
        
        <p class="email-paragraph-small">
          You will receive updates as the order progresses.
        </p>
      `,
    },
    cta: {
      BROKERAGE_FIRM: { text: " View Order", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      BROKERAGE_TEAM_MEMBER: { text: " View Order", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      SIGNAGE_FIRM: { text: " View Assigned Order", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      AGENT: { text: " Track My Orders", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
    },
    footer: "RealEsy. All rights reserved."
  },
  "Brokerage Sales order status update": {
    subject: {
      BROKERAGE_FIRM: " Order #{{orderNumber}} Has Been Shipped",
      BROKERAGE_TEAM_MEMBER: " Order #{{orderNumber}} Has Been Shipped",
      SIGNAGE_FIRM: " Order Status Updated to Shipped – #{{orderNumber}}",
      AGENT: " Your Order #{{orderNumber}} Has Been Shipped",
    },
    title: "Order Shipped",
    logoUrl: "", // Logo is imported directly in emailTemplate.ts
    headerBgColor: "#ffffff",
    bodyLines: [], // Using bodyHtml instead
    bodyHtml: {
      BROKERAGE_FIRM: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          The signage supplier has marked Order #{{orderNumber}} as Shipped. Below are the shipping details:
        </p>
        
        <h3 class="email-heading-h3">
           Shipping Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">#{{orderNumber}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent (if applicable)</td>
              <td class="email-order-details-value-bold">{{agentName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Shipped By</td>
              <td class="email-order-details-value-bold">{{supplierName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Shipped On</td>
              <td class="email-order-details-value-bold">{{shippedOn}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Tracking Number</td>
              <td class="email-order-details-value-bold">{{trackingNumberHtml}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Destination</td>
              <td class="email-order-details-value-bold">{{destination}}</td>
            </tr>
          </table>
        </div>
        
        {{lineItems}}
        
        <p class="email-paragraph-small">
          You can track the status and delivery confirmation in your dashboard.
        </p>
      `,
      BROKERAGE_TEAM_MEMBER: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          The signage supplier has marked Order #{{orderNumber}} as Shipped. Below are the shipping details:
        </p>
        
        <h3 class="email-heading-h3">
           Shipping Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">#{{orderNumber}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent (if applicable)</td>
              <td class="email-order-details-value-bold">{{agentName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Shipped By</td>
              <td class="email-order-details-value-bold">{{supplierName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Shipped On</td>
              <td class="email-order-details-value-bold">{{shippedOn}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Tracking Number</td>
              <td class="email-order-details-value-bold">{{trackingNumberHtml}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Destination</td>
              <td class="email-order-details-value-bold">{{destination}}</td>
            </tr>
          </table>
        </div>
        
        {{lineItems}}
        
        <p class="email-paragraph-small">
          You can track the status and delivery confirmation in your dashboard.
        </p>
      `,
      SIGNAGE_FIRM: `
        <h2 class="email-heading-h2">
          Hi {{supplierName}},
        </h2>
        <p class="email-paragraph">
          You've successfully marked Order #{{orderNumber}} as Shipped. The brokerage and agent have been notified.
        </p>
        
        <p class="email-paragraph-small">
          You can update production or delivery status as needed from your dashboard.
        </p>
      `,
      AGENT: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          Your signage order #{{orderNumber}} has been shipped by {{supplierName}}.
        </p>
        
        <h3 class="email-heading-h3">
           Shipping Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order Date</td>
              <td class="email-order-details-value-bold-first">{{orderDate}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Tracking</td>
              <td class="email-order-details-value-bold">{{trackingNumberHtml}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Destination</td>
              <td class="email-order-details-value-bold">{{destination}}</td>
            </tr>
          </table>
        </div>
        
        {{lineItems}}
        
        <p class="email-paragraph-small">
          You can track the status anytime from your dashboard.
        </p>
      `,
    },
    cta: {
      BROKERAGE_FIRM: { text: " Track Order in Dashboard", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      BROKERAGE_TEAM_MEMBER: { text: " Track Order in Dashboard", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      SIGNAGE_FIRM: { text: " Go to Dashboard", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      AGENT: { text: " View My Orders", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
    },
    footer: "RealEsy. All rights reserved."
  },
  "Brokerage New Team member Activation": {
    subject: {
      BROKERAGE_FIRM: " New Team Member Activated in Your Brokerage Account",
      BROKERAGE_TEAM_MEMBER: "Welcome to Realesy – Your Team Access Is Ready",
    },
    title: "Team Member Activation",
    logoUrl: "", // Logo is imported directly in emailTemplate.ts
    headerBgColor: "#ffffff",
    bodyLines: [], // Using bodyHtml instead
    bodyHtml: {
      BROKERAGE_FIRM: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          A new team member has been successfully activated under your brokerage account.
        </p>
        
        <h3 class="email-heading-h3">
          Team Member Details
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Name</td>
              <td class="email-order-details-value-bold-first">{{teamMemberName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Email</td>
              <td class="email-order-details-value-link">{{email}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Role</td>
              <td class="email-order-details-value-bold">{{role}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Office associated</td>
              <td class="email-order-details-value-bold">{{officeName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Added On</td>
              <td class="email-order-details-value-bold">{{addedOn}}</td>
            </tr>
          </table>
        </div>
        
        <p class="email-paragraph-small">
          You can review their profile, assign tasks, or manage permissions from your dashboard.
        </p>
      `,
      BROKERAGE_TEAM_MEMBER: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          Welcome to Realesy — you've been added to the <strong>{{brokerageName}}</strong> team.
        </p>
        <p class="email-paragraph">
          Your account is now active, and you can log in to access your dashboard, view signage orders, and manage your tasks based on your role.
        </p>
        
        <h3 class="email-heading-h3">
          Your Account Details
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Name</td>
              <td class="email-order-details-value-bold-first">{{teamMemberName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Role</td>
              <td class="email-order-details-value-bold">{{role}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Brokerage</td>
              <td class="email-order-details-value-bold">{{brokerageName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Office associated</td>
              <td class="email-order-details-value-bold">{{officeName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Login URL</td>
              <td class="email-order-details-value-link">{{loginUrl}}</td>
            </tr>
          </table>
        </div>
        
        <p class="email-paragraph-small">
          Need help getting started? Visit our Help Center or contact support@realesy.com.
        </p>
        <p class="email-paragraph-small">
          We're glad you're here!
        </p>
      `,
    },
    cta: {
      BROKERAGE_FIRM: { text: " Manage Team", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      BROKERAGE_TEAM_MEMBER: { text: " Log In to RealEsy", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
    },
    footer: "RealEsy. All rights reserved."
  },
  "Brokerage New Office created": {
    subject: {
      BROKERAGE_FIRM: " New Office Added to Your Realesy Account",
      BROKERAGE_TEAM_MEMBER: " You've Been Assigned to an Office in Realesy",
    },
    title: "New Office Created",
    logoUrl: "", // Logo is imported directly in emailTemplate.ts
    headerBgColor: "#ffffff",
    bodyLines: [], // Using bodyHtml instead
    bodyHtml: {
      BROKERAGE_FIRM: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          A new office has been successfully created under your brokerage account.
        </p>
        
        <h3 class="email-heading-h3">
           Office Details
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Office Name</td>
              <td class="email-order-details-value-bold-first">{{officeName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Created By</td>
              <td class="email-order-details-value-bold">{{createdBy}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Created On</td>
              <td class="email-order-details-value-bold">{{createdOn}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Location</td>
              <td class="email-order-details-value-bold">{{location}}</td>
            </tr>
          </table>
        </div>
        
        <p class="email-paragraph-small">
          You can now manage agents, assign orders, and monitor activity from this office in your dashboard.
        </p>
      `,
      BROKERAGE_TEAM_MEMBER: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          You've been assigned to <strong>{{officeName}}</strong> under your brokerage account in Realesy.
        </p>
        <p class="email-paragraph">
          This office assignment allows you to place orders, manage listings, or collaborate with teammates tied to this location.
        </p>
        
        <h3 class="email-heading-h3">
           Details
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Office Name</td>
              <td class="email-order-details-value-bold-first">{{officeName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Assigned By</td>
              <td class="email-order-details-value-bold">{{adminName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Assignment Date</td>
              <td class="email-order-details-value-bold">{{assignmentDate}}</td>
            </tr>
          </table>
        </div>
        
        <p class="email-paragraph-small">
          If you believe this is incorrect, please reach out to your brokerage admin.
        </p>
      `,
    },
    cta: {
      BROKERAGE_FIRM: { text: " Go to Office Management", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      BROKERAGE_TEAM_MEMBER: { text: " View Dashboard", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
    },
    footer: "RealEsy. All rights reserved."
  },
  "Brokerage Office Assigned": {
    subject: {
      BROKERAGE_FIRM: "Team Member Assigned to Office",
      BROKERAGE_TEAM_MEMBER: " You've Been Assigned to an Office in Realesy",
    },
    title: "Office Assigned",
    logoUrl: "", // Logo is imported directly in emailTemplate.ts
    headerBgColor: "#ffffff",
    bodyLines: [], // Using bodyHtml instead
    bodyHtml: {
      BROKERAGE_FIRM: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          A team member has been successfully assigned to an office under your brokerage.
        </p>
        
        <h3 class="email-heading-h3">
           Assignment Details
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Team Member</td>
              <td class="email-order-details-value-bold-first">{{teamMemberName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Assigned Office</td>
              <td class="email-order-details-value-bold">{{officeName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Assigned On</td>
              <td class="email-order-details-value-bold">{{assignedOn}}</td>
            </tr>
          </table>
        </div>
        
        <p class="email-paragraph-small">
          You can view or update office assignments anytime in your team management dashboard.
        </p>
      `,
      BROKERAGE_TEAM_MEMBER: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          You've been assigned to <strong>{{officeName}}</strong> under your brokerage account in Realesy.
        </p>
        <p class="email-paragraph">
          This office assignment allows you to place orders, manage listings, or collaborate with teammates tied to this location.
        </p>
        
        <h3 class="email-heading-h3">
           Details
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Office Name</td>
              <td class="email-order-details-value-bold-first">{{officeName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Assigned By</td>
              <td class="email-order-details-value-bold">{{adminName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Assignment Date</td>
              <td class="email-order-details-value-bold">{{assignmentDate}}</td>
            </tr>
          </table>
        </div>
        
        <p class="email-paragraph-small">
          If you believe this is incorrect, please reach out to your brokerage admin.
        </p>
      `,
    },
    cta: {
      BROKERAGE_FIRM: { text: " Manage Offices", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      BROKERAGE_TEAM_MEMBER: { text: " View Dashboard", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
    },
    footer: "RealEsy. All rights reserved."
  },
  "Brokerage Password changed": {
    subject: {
      BROKERAGE_FIRM: "Your Realesy Password Has Been Updated",
      BROKERAGE_TEAM_MEMBER: "Your Realesy Password Has Been Updated",
    },
    title: "Password Changed",
    logoUrl: "", // Logo is imported directly in emailTemplate.ts
    headerBgColor: "#ffffff",
    bodyLines: [], // Using bodyHtml instead
    bodyHtml: {
      BROKERAGE_FIRM: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          This is a confirmation that your Realesy password has been successfully updated.
        </p>
        <p class="email-paragraph">
          If you made this change, no further action is needed.
        </p>
        
        <h3 class="email-heading-h3">
           Security Tip:
        </h3>
        <p class="email-paragraph-small">
          If you didn't update your password, please reset it immediately using the link below and notify your admin if needed.
        </p>
      `,
      BROKERAGE_TEAM_MEMBER: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          This is a confirmation that your Realesy password has been successfully updated.
        </p>
        <p class="email-paragraph">
          If you made this change, no further action is needed.
        </p>
        
        <h3 class="email-heading-h3">
           Security Tip:
        </h3>
        <p class="email-paragraph-small">
          If you didn't update your password, please reset it immediately using the link below and notify your admin if needed.
        </p>
      `,
    },
    cta: {
      BROKERAGE_FIRM: { text: " Reset Password Again", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      BROKERAGE_TEAM_MEMBER: { text: " Reset Password Again", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
    },
    footer: "RealEsy. All rights reserved."
  },
  "Brokerage Installation order Created": {
    subject: {
      BROKERAGE_FIRM: "️ New Installation Order Created – #{{orderNumber}}",
      BROKERAGE_TEAM_MEMBER: "️ New Installation Order Created – #{{orderNumber}}",
      SIGNAGE_FIRM: " New Installation Order Assigned – #{{orderNumber}}",
      AGENT: " Your Installation Order Has Been Submitted – #{{orderNumber}}",
    },
    title: "Installation Order Created",
    logoUrl: "",
    headerBgColor: "#ffffff",
    bodyLines: [],
    bodyHtml: {
      BROKERAGE_FIRM: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          A new installation order has been submitted under {{brokerageFirmName}}. Below are the details for your records:
        </p>
        
        <h3 class="email-heading-h3">
           Installation Order Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">#{{orderNumber}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Order Date</td>
              <td class="email-order-details-value-bold">{{orderDate}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent (if applicable)</td>
              <td class="email-order-details-value-bold">{{agentName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Assigned Supplier</td>
              <td class="email-order-details-value-bold">{{supplierName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Installation Address</td>
              <td class="email-order-details-value-bold">{{installationAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Requested Install Date</td>
              <td class="email-order-details-value-bold">{{requestedInstallDate}}</td>
            </tr>
          </table>
        </div>
        
        {{lineItems}}
        
        <p class="email-paragraph-small">
          You can view the full installation details in the Realesy portal.
        </p>
      `,
      BROKERAGE_TEAM_MEMBER: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          A new installation order has been submitted under {{brokerageFirmName}}. Below are the details for your records:
        </p>
        
        <h3 class="email-heading-h3">
           Installation Order Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">#{{orderNumber}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Order Date</td>
              <td class="email-order-details-value-bold">{{orderDate}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent (if applicable)</td>
              <td class="email-order-details-value-bold">{{agentName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Assigned Supplier</td>
              <td class="email-order-details-value-bold">{{supplierName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Installation Address</td>
              <td class="email-order-details-value-bold">{{installationAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Requested Install Date</td>
              <td class="email-order-details-value-bold">{{requestedInstallDate}}</td>
            </tr>
          </table>
        </div>
        
        {{lineItems}}
        
        <p class="email-paragraph-small">
          You can view the full installation details in the Realesy portal.
        </p>
      `,
      SIGNAGE_FIRM: `
        <h2 class="email-heading-h2">
          Hi {{supplierName}},
        </h2>
        <p class="email-paragraph">
          A new installation order has been assigned to your company by {{brokerageFirmName}}.
        </p>
        
        <h3 class="email-heading-h3">
          ️ Order Information
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">#{{orderNumber}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Install Location</td>
              <td class="email-order-details-value-bold">{{installationAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Scheduled Date</td>
              <td class="email-order-details-value-bold">{{requestedInstallDate}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent (if any)</td>
              <td class="email-order-details-value-bold">{{agentName}}</td>
            </tr>
          </table>
        </div>
        
        {{lineItems}}
        
        <p class="email-paragraph-small">
          Please review the order in your Realesy supplier portal and update the status as you proceed.
        </p>
      `,
      AGENT: `
        <h2 class="email-heading-h2">
          Hi {{agentFirstName}},
        </h2>
        <p class="email-paragraph">
          Your installation order has been submitted and assigned to {{supplierName}}.
        </p>
        
        <h3 class="email-heading-h3">
           Order Snapshot
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">#{{orderNumber}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Install Address</td>
              <td class="email-order-details-value-bold">{{installationAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Install Date</td>
              <td class="email-order-details-value-bold">{{requestedInstallDate}}</td>
            </tr>
          </table>
        </div>
        
        {{lineItems}}
        
        <p class="email-paragraph-small">
          You can track the installation progress anytime from your Realesy portal.
        </p>
      `,
    },
    cta: {
      BROKERAGE_FIRM: { text: " Open Installation Order", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      BROKERAGE_TEAM_MEMBER: { text: " Open Installation Order", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      SIGNAGE_FIRM: { text: " Access Installation Orders", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      AGENT: { text: " View My Orders", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
    },
    footer: "RealEsy. All rights reserved."
  },
  "Brokerage Installation order Scheduled": {
    subject: {
      BROKERAGE_FIRM: " Installation Scheduled – Order #{{orderNumber}}",
      BROKERAGE_TEAM_MEMBER: " Installation Scheduled – Order #{{orderNumber}}",
      AGENT: "️ Installation Scheduled for Your Order #{{orderNumber}}",
    },
    title: "Installation Scheduled",
    logoUrl: "",
    headerBgColor: "#ffffff",
    bodyLines: [],
    bodyHtml: {
      BROKERAGE_FIRM: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          The supplier has scheduled an installation for Order #{{orderNumber}} under {{brokerageFirmName}}. Here are the updated details:
        </p>
        
        <h3 class="email-heading-h3">
          ️ Installation Schedule
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">#{{orderNumber}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Scheduled Date</td>
              <td class="email-order-details-value-bold">{{scheduledDate}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Install Address</td>
              <td class="email-order-details-value-bold">{{installationAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Supplier</td>
              <td class="email-order-details-value-bold">{{supplierName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent (if any)</td>
              <td class="email-order-details-value-bold">{{agentName}}</td>
            </tr>
          </table>
        </div>
        
        <p class="email-paragraph-small">
          You can review all upcoming installs from the Realesy portal.
        </p>
      `,
      BROKERAGE_TEAM_MEMBER: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          The supplier has scheduled an installation for Order #{{orderNumber}} under {{brokerageFirmName}}. Here are the updated details:
        </p>
        
        <h3 class="email-heading-h3">
          ️ Installation Schedule
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">#{{orderNumber}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Scheduled Date</td>
              <td class="email-order-details-value-bold">{{scheduledDate}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Install Address</td>
              <td class="email-order-details-value-bold">{{installationAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Supplier</td>
              <td class="email-order-details-value-bold">{{supplierName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent (if any)</td>
              <td class="email-order-details-value-bold">{{agentName}}</td>
            </tr>
          </table>
        </div>
        
        <p class="email-paragraph-small">
          You can review all upcoming installs from the Realesy portal.
        </p>
      `,
      AGENT: `
        <h2 class="email-heading-h2">
          Hi {{agentFirstName}},
        </h2>
        <p class="email-paragraph">
          The installation for your signage order #{{orderNumber}} has been scheduled.
        </p>
        
        <h3 class="email-heading-h3">
           Scheduled Details
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Install Date</td>
              <td class="email-order-details-value-bold-first">{{scheduledDate}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value-bold">{{installationAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Supplier</td>
              <td class="email-order-details-value-bold">{{supplierName}}</td>
            </tr>
          </table>
        </div>
        
        <p class="email-paragraph-small">
          You'll receive an update once the installation is complete.
        </p>
      `,
    },
    cta: {
      BROKERAGE_FIRM: { text: " View Installation Schedule", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      BROKERAGE_TEAM_MEMBER: { text: " View Installation Schedule", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      AGENT: { text: " View My Orders", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
    },
    footer: "RealEsy. All rights reserved."
  },
  "Brokerage Installation order Completed": {
    subject: {
      BROKERAGE_FIRM: "Installation Completed – Order #{{orderNumber}}",
      BROKERAGE_TEAM_MEMBER: "Installation Completed – Order #{{orderNumber}}",
      AGENT: "Your Installation Order #{{orderNumber}} Is Complete",
    },
    title: "Installation Completed",
    logoUrl: "",
    headerBgColor: "#ffffff",
    bodyLines: [],
    bodyHtml: {
      BROKERAGE_FIRM: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          The installation for Order #{{orderNumber}} has been marked as completed by the supplier. See the installation summary below:
        </p>
        
        <h3 class="email-heading-h3">
           Installation Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">#{{orderNumber}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Completed On</td>
              <td class="email-order-details-value-bold">{{completedDate}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Installed By</td>
              <td class="email-order-details-value-bold">{{supplierName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Address</td>
              <td class="email-order-details-value-bold">{{installationAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent (if any)</td>
              <td class="email-order-details-value-bold">{{agentName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Installer Notes</td>
              <td class="email-order-details-value-bold">{{installerNotes}}</td>
            </tr>
          </table>
        </div>
        
        <p class="email-paragraph-small">
          You can view the full order record in your Realesy portal, including installation photos and final notes.
        </p>
      `,
      BROKERAGE_TEAM_MEMBER: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          The installation for Order #{{orderNumber}} has been marked as completed by the supplier. See the installation summary below:
        </p>
        
        <h3 class="email-heading-h3">
           Installation Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">#{{orderNumber}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Completed On</td>
              <td class="email-order-details-value-bold">{{completedDate}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Installed By</td>
              <td class="email-order-details-value-bold">{{supplierName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Address</td>
              <td class="email-order-details-value-bold">{{installationAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent (if any)</td>
              <td class="email-order-details-value-bold">{{agentName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Installer Notes</td>
              <td class="email-order-details-value-bold">{{installerNotes}}</td>
            </tr>
          </table>
        </div>
        
        <p class="email-paragraph-small">
          You can view the full order record in your Realesy portal, including installation photos and final notes.
        </p>
      `,
      AGENT: `
        <h2 class="email-heading-h2">
          Hi {{agentFirstName}},
        </h2>
        <p class="email-paragraph">
          Your signage order #{{orderNumber}} has been successfully installed.
        </p>
        
        <h3 class="email-heading-h3">
           Installation Snapshot
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Installed On</td>
              <td class="email-order-details-value-bold-first">{{completedDate}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Location</td>
              <td class="email-order-details-value-bold">{{installationAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Supplier</td>
              <td class="email-order-details-value-bold">{{supplierName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Notes</td>
              <td class="email-order-details-value-bold">{{installerNotes}}</td>
            </tr>
          </table>
        </div>
        
        <p class="email-paragraph-small">
          You can view or download installation photos directly from your Realesy portal.
        </p>
      `,
    },
    cta: {
      BROKERAGE_FIRM: { text: " View Completed Order", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      BROKERAGE_TEAM_MEMBER: { text: " View Completed Order", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      AGENT: { text: " See Installation", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
    },
    footer: "RealEsy. All rights reserved."
  },
  "Brokerage Installation order Canceled": {
    subject: {
      BROKERAGE_FIRM: " Installation Order Canceled – #{{orderNumber}}",
      BROKERAGE_TEAM_MEMBER: " Installation Order Canceled – #{{orderNumber}}",
      AGENT: " Your Installation Order #{{orderNumber}} Was Canceled",
    },
    title: "Installation Order Canceled",
    logoUrl: "",
    headerBgColor: "#ffffff",
    bodyLines: [],
    bodyHtml: {
      BROKERAGE_FIRM: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          Installation Order #{{orderNumber}} has been canceled. Below are the details for your records:
        </p>
        
        <h3 class="email-heading-h3">
           Order Cancellation Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">#{{orderNumber}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Canceled On</td>
              <td class="email-order-details-value-bold">{{canceledDate}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Canceled By</td>
              <td class="email-order-details-value-bold">{{canceledBy}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent (if any)</td>
              <td class="email-order-details-value-bold">{{agentName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Address</td>
              <td class="email-order-details-value-bold">{{installationAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Reason (if provided)</td>
              <td class="email-order-details-value-bold">{{cancellationReason}}</td>
            </tr>
          </table>
        </div>
        
        <p class="email-paragraph-small">
          You can review or reinitiate the order anytime from your Realesy portal.
        </p>
      `,
      BROKERAGE_TEAM_MEMBER: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          Installation Order #{{orderNumber}} has been canceled. Below are the details for your records:
        </p>
        
        <h3 class="email-heading-h3">
           Order Cancellation Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">#{{orderNumber}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Canceled On</td>
              <td class="email-order-details-value-bold">{{canceledDate}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Canceled By</td>
              <td class="email-order-details-value-bold">{{canceledBy}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent (if any)</td>
              <td class="email-order-details-value-bold">{{agentName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Address</td>
              <td class="email-order-details-value-bold">{{installationAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Reason (if provided)</td>
              <td class="email-order-details-value-bold">{{cancellationReason}}</td>
            </tr>
          </table>
        </div>
        
        <p class="email-paragraph-small">
          You can review or reinitiate the order anytime from your Realesy portal.
        </p>
      `,
      AGENT: `
        <h2 class="email-heading-h2">
          Hi {{agentFirstName}},
        </h2>
        <p class="email-paragraph">
          Your installation order #{{orderNumber}} has been canceled. Below is a summary:
        </p>
        
        <h3 class="email-heading-h3">
           Canceled Order Details
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">#{{orderNumber}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property</td>
              <td class="email-order-details-value-bold">{{installationAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Canceled On</td>
              <td class="email-order-details-value-bold">{{canceledDate}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Reason</td>
              <td class="email-order-details-value-bold">{{cancellationReason}}</td>
            </tr>
          </table>
        </div>
        
        <p class="email-paragraph-small">
          If this was unintentional, you can request a new installation from your Realesy portal.
        </p>
      `,
    },
    cta: {
      BROKERAGE_FIRM: { text: " Review Canceled Orders", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      BROKERAGE_TEAM_MEMBER: { text: " Review Canceled Orders", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      AGENT: { text: " Place New Order", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
    },
    footer: "RealEsy. All rights reserved."
  },
  "Brokerage Removal request Created": {
    subject: {
      BROKERAGE_FIRM: " Removal Request Submitted – Order #{{orderNumber}}",
      BROKERAGE_TEAM_MEMBER: " Removal Request Submitted – Order #{{orderNumber}}",
      SIGNAGE_FIRM: " New Signage Removal Request – #{{orderNumber}}",
      SIGNAGE_TEAM_MEMBER: " New Signage Removal Request – #{{orderNumber}}",
      AGENT: " Your Signage Removal Request Was Submitted",
    },
    title: "Removal Request Created",
    logoUrl: "",
    headerBgColor: "#ffffff",
    bodyLines: [],
    bodyHtml: {
      BROKERAGE_FIRM: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          A signage removal request has been submitted under your brokerage account. Below are the request details:
        </p>
        
        <h3 class="email-heading-h3">
           Removal Request Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">#{{orderNumber}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Request Date</td>
              <td class="email-order-details-value-bold">{{requestDate}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Requested By</td>
              <td class="email-order-details-value-bold">{{requestedBy}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value-bold">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Assigned Supplier</td>
              <td class="email-order-details-value-bold">{{supplierName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Reason (Optional)</td>
              <td class="email-order-details-value-bold">{{reason}}</td>
            </tr>
          </table>
        </div>
        
        <p class="email-paragraph-small">
          You can track the request status in your Realesy portal.
        </p>
      `,
      BROKERAGE_TEAM_MEMBER: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          A signage removal request has been submitted under your brokerage account. Below are the request details:
        </p>
        
        <h3 class="email-heading-h3">
           Removal Request Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">#{{orderNumber}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Request Date</td>
              <td class="email-order-details-value-bold">{{requestDate}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Requested By</td>
              <td class="email-order-details-value-bold">{{requestedBy}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value-bold">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Assigned Supplier</td>
              <td class="email-order-details-value-bold">{{supplierName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Reason (Optional)</td>
              <td class="email-order-details-value-bold">{{reason}}</td>
            </tr>
          </table>
        </div>
        
        <p class="email-paragraph-small">
          You can track the request status in your Realesy portal.
        </p>
      `,
      SIGNAGE_FIRM: `
        <h2 class="email-heading-h2">
          Hi {{supplierFirstName}},
        </h2>
        <p class="email-paragraph">
          A signage removal request has been assigned to your team from {{brokerageFirmName}}. Please find the details below:
        </p>
        
        <h3 class="email-heading-h3">
          ️ Removal Order Details
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">#{{orderNumber}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value-bold">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Requested By</td>
              <td class="email-order-details-value-bold">{{requestedBy}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Date Submitted</td>
              <td class="email-order-details-value-bold">{{requestDate}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Priority</td>
              <td class="email-order-details-value-bold">{{priority}}</td>
            </tr>
          </table>
        </div>
        
        <p class="email-paragraph-small">
          Please update the request as it progresses.
        </p>
      `,
      SIGNAGE_TEAM_MEMBER: `
        <h2 class="email-heading-h2">
          Hi {{supplierFirstName}},
        </h2>
        <p class="email-paragraph">
          A signage removal request has been assigned to your team from {{brokerageFirmName}}. Please find the details below:
        </p>
        
        <h3 class="email-heading-h3">
          ️ Removal Order Details
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">#{{orderNumber}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value-bold">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Requested By</td>
              <td class="email-order-details-value-bold">{{requestedBy}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Date Submitted</td>
              <td class="email-order-details-value-bold">{{requestDate}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Priority</td>
              <td class="email-order-details-value-bold">{{priority}}</td>
            </tr>
          </table>
        </div>
        
        <p class="email-paragraph-small">
          Please update the request as it progresses.
        </p>
      `,
      AGENT: `
        <h2 class="email-heading-h2">
          Hi {{agentFirstName}},
        </h2>
        <p class="email-paragraph">
          Your request to remove signage from the following property has been submitted successfully.
        </p>
        
        <h3 class="email-heading-h3">
           Removal Request Snapshot
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">#{{orderNumber}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value-bold">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Submitted On</td>
              <td class="email-order-details-value-bold">{{requestDate}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Supplier Assigned</td>
              <td class="email-order-details-value-bold">{{supplierName}}</td>
            </tr>
          </table>
        </div>
        
        <p class="email-paragraph-small">
          You'll receive a notification once the signage has been removed.
        </p>
      `,
    },
    cta: {
      BROKERAGE_FIRM: { text: " View Removal Requests", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      BROKERAGE_TEAM_MEMBER: { text: " View Removal Requests", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      SIGNAGE_FIRM: { text: " Access Removal Queue", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      SIGNAGE_TEAM_MEMBER: { text: " Access Removal Queue", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      AGENT: { text: " Track My Orders", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
    },
    footer: "RealEsy. All rights reserved."
  },
  "Brokerage Removal order Scheduled": {
    subject: {
      BROKERAGE_FIRM: "️ Removal Scheduled – Order #{{orderNumber}}",
      BROKERAGE_TEAM_MEMBER: "️ Removal Scheduled – Order #{{orderNumber}}",
      AGENT: "️ Your Signage Removal Is Scheduled – #{{orderNumber}}",
    },
    title: "Removal Scheduled",
    logoUrl: "",
    headerBgColor: "#ffffff",
    bodyLines: [],
    bodyHtml: {
      BROKERAGE_FIRM: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          The signage removal for Order #{{orderNumber}} has been scheduled by the supplier. Please see the scheduled details below:
        </p>
        
        <h3 class="email-heading-h3">
           Scheduled Removal Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">#{{orderNumber}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Scheduled Date</td>
              <td class="email-order-details-value-bold">{{scheduledDate}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Time Window</td>
              <td class="email-order-details-value-bold">{{timeWindow}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value-bold">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Assigned Supplier</td>
              <td class="email-order-details-value-bold">{{supplierName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent (if any)</td>
              <td class="email-order-details-value-bold">{{agentName}}</td>
            </tr>
          </table>
        </div>
        
        <p class="email-paragraph-small">
          You can view the updated schedule in the Realesy portal.
        </p>
      `,
      BROKERAGE_TEAM_MEMBER: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          The signage removal for Order #{{orderNumber}} has been scheduled by the supplier. Please see the scheduled details below:
        </p>
        
        <h3 class="email-heading-h3">
           Scheduled Removal Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">#{{orderNumber}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Scheduled Date</td>
              <td class="email-order-details-value-bold">{{scheduledDate}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Time Window</td>
              <td class="email-order-details-value-bold">{{timeWindow}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value-bold">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Assigned Supplier</td>
              <td class="email-order-details-value-bold">{{supplierName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent (if any)</td>
              <td class="email-order-details-value-bold">{{agentName}}</td>
            </tr>
          </table>
        </div>
        
        <p class="email-paragraph-small">
          You can view the updated schedule in the Realesy portal.
        </p>
      `,
      AGENT: `
        <h2 class="email-heading-h2">
          Hi {{agentFirstName}},
        </h2>
        <p class="email-paragraph">
          The signage for your property is scheduled to be removed. See the details below:
        </p>
        
        <h3 class="email-heading-h3">
           Scheduled Removal
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">#{{orderNumber}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Scheduled Date</td>
              <td class="email-order-details-value-bold">{{scheduledDate}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Time Window</td>
              <td class="email-order-details-value-bold">{{timeWindow}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Location</td>
              <td class="email-order-details-value-bold">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Supplier</td>
              <td class="email-order-details-value-bold">{{supplierName}}</td>
            </tr>
          </table>
        </div>
        
        <p class="email-paragraph-small">
          You'll receive a confirmation once the removal is completed.
        </p>
      `,
    },
    cta: {
      BROKERAGE_FIRM: { text: " View Scheduled Removals", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      BROKERAGE_TEAM_MEMBER: { text: " View Scheduled Removals", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      AGENT: { text: " Track My Orders", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
    },
    footer: "RealEsy. All rights reserved."
  },
  "Brokerage Removal order Completed": {
    subject: {
      BROKERAGE_FIRM: "Signage Removal Completed – Order #{{orderNumber}}",
      BROKERAGE_TEAM_MEMBER: "Signage Removal Completed – Order #{{orderNumber}}",
      AGENT: "Your Signage Has Been Removed – Order #{{orderNumber}}",
    },
    title: "Removal Completed",
    logoUrl: "",
    headerBgColor: "#ffffff",
    bodyLines: [],
    bodyHtml: {
      BROKERAGE_FIRM: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          The signage supplier has marked Order #{{orderNumber}} as completed. The signage has been removed from the property as scheduled.
        </p>
        
        <h3 class="email-heading-h3">
           Removal Completion Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">#{{orderNumber}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Completed On</td>
              <td class="email-order-details-value-bold">{{completedDate}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value-bold">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Removed By</td>
              <td class="email-order-details-value-bold">{{supplierName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent (if any)</td>
              <td class="email-order-details-value-bold">{{agentName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Photos Provided</td>
              <td class="email-order-details-value-bold">{{photosProvided}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Notes from Installer</td>
              <td class="email-order-details-value-bold">{{installerNotes}}</td>
            </tr>
          </table>
        </div>
        
        <p class="email-paragraph-small">
          You can review the completed order in your Realesy portal.
        </p>
      `,
      BROKERAGE_TEAM_MEMBER: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          The signage supplier has marked Order #{{orderNumber}} as completed. The signage has been removed from the property as scheduled.
        </p>
        
        <h3 class="email-heading-h3">
           Removal Completion Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">#{{orderNumber}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Completed On</td>
              <td class="email-order-details-value-bold">{{completedDate}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value-bold">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Removed By</td>
              <td class="email-order-details-value-bold">{{supplierName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent (if any)</td>
              <td class="email-order-details-value-bold">{{agentName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Photos Provided</td>
              <td class="email-order-details-value-bold">{{photosProvided}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Notes from Installer</td>
              <td class="email-order-details-value-bold">{{installerNotes}}</td>
            </tr>
          </table>
        </div>
        
        <p class="email-paragraph-small">
          You can review the completed order in your Realesy portal.
        </p>
      `,
      AGENT: `
        <h2 class="email-heading-h2">
          Hi {{agentFirstName}},
        </h2>
        <p class="email-paragraph">
          Your signage removal request for Order #{{orderNumber}} has been completed by {{supplierName}}.
        </p>
        
        <h3 class="email-heading-h3">
           Removal Details
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">#{{orderNumber}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Completed On</td>
              <td class="email-order-details-value-bold">{{completedDate}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Location</td>
              <td class="email-order-details-value-bold">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Photos Included</td>
              <td class="email-order-details-value-bold">{{photosProvided}}</td>
            </tr>
          </table>
        </div>
        
        <p class="email-paragraph-small">
          You can view the removal details and any photos in your Realesy portal.
        </p>
      `,
    },
    cta: {
      BROKERAGE_FIRM: { text: " View Completed Removals", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      BROKERAGE_TEAM_MEMBER: { text: " View Completed Removals", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      AGENT: { text: " View My Orders", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
    },
    footer: "RealEsy. All rights reserved."
  },
  "Brokerage Removal order Canceled": {
    subject: {
      BROKERAGE_FIRM: " Signage Removal Canceled – Order #{{orderNumber}}",
      BROKERAGE_TEAM_MEMBER: " Signage Removal Canceled – Order #{{orderNumber}}",
      AGENT: " Your Signage Removal Request Was Canceled – Order #{{orderNumber}}",
    },
    title: "Removal Canceled",
    logoUrl: "",
    headerBgColor: "#ffffff",
    bodyLines: [],
    bodyHtml: {
      BROKERAGE_FIRM: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          The signage removal for Order #{{orderNumber}} has been canceled. Below is the cancellation summary for your reference:
        </p>
        
        <h3 class="email-heading-h3">
          ️ Cancellation Details
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">#{{orderNumber}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Canceled On</td>
              <td class="email-order-details-value-bold">{{canceledDate}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Canceled By</td>
              <td class="email-order-details-value-bold">{{canceledBy}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value-bold">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent (if any)</td>
              <td class="email-order-details-value-bold">{{agentName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Reason (if provided)</td>
              <td class="email-order-details-value-bold">{{reason}}</td>
            </tr>
          </table>
        </div>
        
        <p class="email-paragraph-small">
          You can review or reinitiate this request in your Realesy portal if needed.
        </p>
      `,
      BROKERAGE_TEAM_MEMBER: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          The signage removal for Order #{{orderNumber}} has been canceled. Below is the cancellation summary for your reference:
        </p>
        
        <h3 class="email-heading-h3">
          ️ Cancellation Details
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">#{{orderNumber}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Canceled On</td>
              <td class="email-order-details-value-bold">{{canceledDate}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Canceled By</td>
              <td class="email-order-details-value-bold">{{canceledBy}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value-bold">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent (if any)</td>
              <td class="email-order-details-value-bold">{{agentName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Reason (if provided)</td>
              <td class="email-order-details-value-bold">{{reason}}</td>
            </tr>
          </table>
        </div>
        
        <p class="email-paragraph-small">
          You can review or reinitiate this request in your Realesy portal if needed.
        </p>
      `,
      AGENT: `
        <h2 class="email-heading-h2">
          Hi {{agentFirstName}},
        </h2>
        <p class="email-paragraph">
          Your signage removal request for Order #{{orderNumber}} has been canceled.
        </p>
        
        <h3 class="email-heading-h3">
           Removal Cancellation Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">#{{orderNumber}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value-bold">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Canceled On</td>
              <td class="email-order-details-value-bold">{{canceledDate}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Reason</td>
              <td class="email-order-details-value-bold">{{reason}}</td>
            </tr>
          </table>
        </div>
        
        <p class="email-paragraph-small">
          If this was a mistake, you can submit a new removal request from your Realesy portal.
        </p>
      `,
    },
    cta: {
      BROKERAGE_FIRM: { text: " View Canceled Removals", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      BROKERAGE_TEAM_MEMBER: { text: " View Canceled Removals", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      AGENT: { text: " Submit New Removal", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
    },
    footer: "RealEsy. All rights reserved."
  },
  "Brokerage Removal order Void": {
    subject: {
      BROKERAGE_FIRM: "️ Removal Order Voided – #{{orderNumber}}",
      BROKERAGE_TEAM_MEMBER: "️ Removal Order Voided – #{{orderNumber}}",
      SIGNAGE_FIRM: "️ Removal Order #{{orderNumber}} Marked as Voided",
      SIGNAGE_TEAM_MEMBER: "️ Removal Order #{{orderNumber}} Marked as Voided",
      AGENT: "️ Your Removal Order Was Voided – #{{orderNumber}}",
    },
    title: "Removal Order Voided",
    logoUrl: "",
    headerBgColor: "#ffffff",
    bodyLines: [],
    bodyHtml: {
      BROKERAGE_FIRM: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          The signage supplier has voided Removal Order #{{orderNumber}}. This order is no longer active. Please review the reason provided below:
        </p>
        
        <h3 class="email-heading-h3">
           Voided Removal Order Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">#{{orderNumber}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Voided On</td>
              <td class="email-order-details-value-bold">{{voidedOn}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Voided By</td>
              <td class="email-order-details-value-bold">{{voidedBy}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value-bold">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent (if any)</td>
              <td class="email-order-details-value-bold">{{agentName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Reason</td>
              <td class="email-order-details-value-bold">{{reason}}</td>
            </tr>
          </table>
        </div>
        
        <p class="email-paragraph-small">
          If this was voided in error, you may submit a new removal request via the Realesy portal.
        </p>
      `,
      BROKERAGE_TEAM_MEMBER: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          The signage supplier has voided Removal Order #{{orderNumber}}. This order is no longer active. Please review the reason provided below:
        </p>
        
        <h3 class="email-heading-h3">
           Voided Removal Order Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">#{{orderNumber}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Voided On</td>
              <td class="email-order-details-value-bold">{{voidedOn}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Voided By</td>
              <td class="email-order-details-value-bold">{{voidedBy}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value-bold">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent (if any)</td>
              <td class="email-order-details-value-bold">{{agentName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Reason</td>
              <td class="email-order-details-value-bold">{{reason}}</td>
            </tr>
          </table>
        </div>
        
        <p class="email-paragraph-small">
          If this was voided in error, you may submit a new removal request via the Realesy portal.
        </p>
      `,
      SIGNAGE_FIRM: `
        <h2 class="email-heading-h2">
          Hi {{supplierFirstName}},
        </h2>
        <p class="email-paragraph">
          You have successfully marked Removal Order #{{orderNumber}} as Voided. This order is now closed and removed from your active queue.
        </p>
        <p class="email-paragraph">
          All relevant stakeholders have been notified.
        </p>
        
        <h3 class="email-heading-h3">
          ️ Void Confirmation
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">#{{orderNumber}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Date Voided</td>
              <td class="email-order-details-value-bold">{{voidedOn}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Notes</td>
              <td class="email-order-details-value-bold">{{reason}}</td>
            </tr>
          </table>
        </div>
      `,
      SIGNAGE_TEAM_MEMBER: `
        <h2 class="email-heading-h2">
          Hi {{supplierFirstName}},
        </h2>
        <p class="email-paragraph">
          You have successfully marked Removal Order #{{orderNumber}} as Voided. This order is now closed and removed from your active queue.
        </p>
        <p class="email-paragraph">
          All relevant stakeholders have been notified.
        </p>
        
        <h3 class="email-heading-h3">
          ️ Void Confirmation
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">#{{orderNumber}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Date Voided</td>
              <td class="email-order-details-value-bold">{{voidedOn}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Notes</td>
              <td class="email-order-details-value-bold">{{reason}}</td>
            </tr>
          </table>
        </div>
      `,
      AGENT: `
        <h2 class="email-heading-h2">
          Hi {{agentFirstName}},
        </h2>
        <p class="email-paragraph">
          Your signage removal order #{{orderNumber}} was voided by the supplier and is no longer active.
        </p>
        
        <h3 class="email-heading-h3">
           Void Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">#{{orderNumber}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Voided By</td>
              <td class="email-order-details-value-bold">{{voidedBy}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property</td>
              <td class="email-order-details-value-bold">{{propertyAddress}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Reason</td>
              <td class="email-order-details-value-bold">{{reason}}</td>
            </tr>
          </table>
        </div>
        
        <p class="email-paragraph-small">
          If needed, you can submit a new request via your Realesy portal.
        </p>
      `,
    },
    cta: {
      BROKERAGE_FIRM: { text: " Submit New Removal Request", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      BROKERAGE_TEAM_MEMBER: { text: " Submit New Removal Request", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      SIGNAGE_FIRM: { text: " View All Orders", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      SIGNAGE_TEAM_MEMBER: { text: " View All Orders", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      AGENT: { text: " Place New Removal Request", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
    },
    footer: "RealEsy. All rights reserved."
  },
  "Brokerage Installation order edit": {
    subject: {
      SIGNAGE_FIRM: "️ Installation Order #{{orderNumber}} Edited Successfully",
      SIGNAGE_TEAM_MEMBER: "️ Installation Order #{{orderNumber}} Edited Successfully",
      BROKERAGE_FIRM: " Installation Order #{{orderNumber}} Was Updated by {{supplierName}}",
      BROKERAGE_TEAM_MEMBER: " Installation Order #{{orderNumber}} Was Updated by {{supplierName}}",
      AGENT: " Your Installation Order #{{orderNumber}} Was Updated",
    },
    title: "Installation Order Edit",
    logoUrl: "",
    headerBgColor: "#ffffff",
    bodyLines: [],
    bodyHtml: {
      SIGNAGE_FIRM: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          You have successfully updated the details for Installation Order #{{orderNumber}}. Below is a summary of the changes:
        </p>
        
        <h3 class="email-heading-h3">
          ️ Order Update Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">#{{orderNumber}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Edited On</td>
              <td class="email-order-details-value-bold">{{editedOn}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Edited By</td>
              <td class="email-order-details-value-bold">{{userName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Updated Fields</td>
              <td class="email-order-details-value-bold">{{updatedFields}}</td>
            </tr>
          </table>
        </div>
        
        <p class="email-paragraph-small">
          All relevant parties have been notified.
        </p>
      `,
      SIGNAGE_TEAM_MEMBER: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          You have successfully updated the details for Installation Order #{{orderNumber}}. Below is a summary of the changes:
        </p>
        
        <h3 class="email-heading-h3">
          ️ Order Update Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">#{{orderNumber}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Edited On</td>
              <td class="email-order-details-value-bold">{{editedOn}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Edited By</td>
              <td class="email-order-details-value-bold">{{userName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Updated Fields</td>
              <td class="email-order-details-value-bold">{{updatedFields}}</td>
            </tr>
          </table>
        </div>
        
        <p class="email-paragraph-small">
          All relevant parties have been notified.
        </p>
      `,
      BROKERAGE_FIRM: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          The signage company {{supplierName}} has made updates to Installation Order #{{orderNumber}}. Please review the changes below:
        </p>
        
        <h3 class="email-heading-h3">
           Updated Installation Details
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">#{{orderNumber}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Edited On</td>
              <td class="email-order-details-value-bold">{{editedOn}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Updated By</td>
              <td class="email-order-details-value-bold">{{userName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Installation Date</td>
              <td class="email-order-details-value-bold">{{installationDate}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Address</td>
              <td class="email-order-details-value-bold">{{installLocation}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Notes</td>
              <td class="email-order-details-value-bold">{{notes}}</td>
            </tr>
          </table>
        </div>
        
        <p class="email-paragraph-small">
          You can view and track the latest order status in your Realesy portal.
        </p>
      `,
      BROKERAGE_TEAM_MEMBER: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          The signage company {{supplierName}} has made updates to Installation Order #{{orderNumber}}. Please review the changes below:
        </p>
        
        <h3 class="email-heading-h3">
           Updated Installation Details
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">#{{orderNumber}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Edited On</td>
              <td class="email-order-details-value-bold">{{editedOn}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Updated By</td>
              <td class="email-order-details-value-bold">{{userName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Installation Date</td>
              <td class="email-order-details-value-bold">{{installationDate}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Address</td>
              <td class="email-order-details-value-bold">{{installLocation}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Notes</td>
              <td class="email-order-details-value-bold">{{notes}}</td>
            </tr>
          </table>
        </div>
        
        <p class="email-paragraph-small">
          You can view and track the latest order status in your Realesy portal.
        </p>
      `,
      AGENT: `
        <h2 class="email-heading-h2">
          Hi {{agentFirstName}},
        </h2>
        <p class="email-paragraph">
          Your signage installation order #{{orderNumber}} has been updated by {{supplierName}}. Below is a summary of the changes:
        </p>
        
        <h3 class="email-heading-h3">
          ️ Updated Order Summary
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field">Install Date</td>
              <td class="email-order-details-value-bold">{{installDate}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Location</td>
              <td class="email-order-details-value-bold">{{address}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Changes Made</td>
              <td class="email-order-details-value-bold">{{changesMade}}</td>
            </tr>
          </table>
        </div>
      `,
    },
    cta: {
      SIGNAGE_FIRM: { text: " View Order in Portal", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      SIGNAGE_TEAM_MEMBER: { text: " View Order in Portal", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      BROKERAGE_FIRM: { text: " Review Updated Order", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      BROKERAGE_TEAM_MEMBER: { text: " Review Updated Order", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      AGENT: { text: " View My Orders", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
    },
    footer: "RealEsy. All rights reserved."
  },
  "Brokerage Removal order edit": {
    subject: {
      SIGNAGE_FIRM: "️ Removal Order #{{orderNumber}} Updated Successfully",
      SIGNAGE_TEAM_MEMBER: "️ Removal Order #{{orderNumber}} Updated Successfully",
      BROKERAGE_FIRM: " Removal Order #{{orderNumber}} Updated by {{supplierName}}",
      BROKERAGE_TEAM_MEMBER: " Removal Order #{{orderNumber}} Updated by {{supplierName}}",
      AGENT: " Update to Your Removal Order #{{orderNumber}}",
    },
    title: "Removal Order Edit",
    logoUrl: "",
    headerBgColor: "#ffffff",
    bodyLines: [],
    bodyHtml: {
      SIGNAGE_FIRM: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          You've successfully updated Removal Order #{{orderNumber}}. The updated details are listed below, and the brokerage and agent (if applicable) have been notified.
        </p>
        
        <h3 class="email-heading-h3">
          ️ Updated Removal Order Details
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">#{{orderNumber}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Edited On</td>
              <td class="email-order-details-value-bold">{{editedOn}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Changed By</td>
              <td class="email-order-details-value-bold">{{userName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Updated Fields</td>
              <td class="email-order-details-value-bold">{{updatedFields}}</td>
            </tr>
          </table>
        </div>
      `,
      SIGNAGE_TEAM_MEMBER: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          You've successfully updated Removal Order #{{orderNumber}}. The updated details are listed below, and the brokerage and agent (if applicable) have been notified.
        </p>
        
        <h3 class="email-heading-h3">
          ️ Updated Removal Order Details
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">Order ID</td>
              <td class="email-order-details-value-bold-first">#{{orderNumber}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Edited On</td>
              <td class="email-order-details-value-bold">{{editedOn}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Changed By</td>
              <td class="email-order-details-value-bold">{{userName}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Updated Fields</td>
              <td class="email-order-details-value-bold">{{updatedFields}}</td>
            </tr>
          </table>
        </div>
      `,
      BROKERAGE_FIRM: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          {{supplierName}} has made changes to Removal Order #{{orderNumber}}. Please review the updated details:
        </p>
        
        <h3 class="email-heading-h3">
          ️ Updated Removal Information
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field">Order ID</td>
              <td class="email-order-details-value-bold">#{{orderNumber}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Date of Change</td>
              <td class="email-order-details-value-bold">{{editedOn}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Scheduled Date</td>
              <td class="email-order-details-value-bold">{{scheduledDate}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value-bold">{{removalLocation}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Notes</td>
              <td class="email-order-details-value-bold">{{notes}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent (if any)</td>
              <td class="email-order-details-value-bold">{{agentName}}</td>
            </tr>
          </table>
        </div>
        
        <p class="email-paragraph-small">
          You can review the updated order in your Realesy portal.
        </p>
      `,
      BROKERAGE_TEAM_MEMBER: `
        <h2 class="email-heading-h2">
          Hi {{firstName}},
        </h2>
        <p class="email-paragraph">
          {{supplierName}} has made changes to Removal Order #{{orderNumber}}. Please review the updated details:
        </p>
        
        <h3 class="email-heading-h3">
          ️ Updated Removal Information
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field">Order ID</td>
              <td class="email-order-details-value-bold">#{{orderNumber}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Date of Change</td>
              <td class="email-order-details-value-bold">{{editedOn}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Scheduled Date</td>
              <td class="email-order-details-value-bold">{{scheduledDate}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Property Address</td>
              <td class="email-order-details-value-bold">{{removalLocation}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Notes</td>
              <td class="email-order-details-value-bold">{{notes}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Agent (if any)</td>
              <td class="email-order-details-value-bold">{{agentName}}</td>
            </tr>
          </table>
        </div>
        
        <p class="email-paragraph-small">
          You can review the updated order in your Realesy portal.
        </p>
      `,
      AGENT: `
        <h2 class="email-heading-h2">
          Hi {{agentFirstName}},
        </h2>
        <p class="email-paragraph">
          Your signage removal order #{{orderNumber}} has been updated by {{supplierName}}.
        </p>
        
        <h3 class="email-heading-h3">
           Latest Removal Details
        </h3>
        
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
<tr>
              <td class="email-order-details-field-first">New Scheduled Date</td>
              <td class="email-order-details-value-bold-first">{{scheduledDate}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Address</td>
              <td class="email-order-details-value-bold">{{installLocation}}</td>
            </tr>
            <tr>
              <td class="email-order-details-field">Notes</td>
              <td class="email-order-details-value-bold">{{notes}}</td>
            </tr>
          </table>
        </div>
      `,
    },
    cta: {
      SIGNAGE_FIRM: { text: " View Order", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      SIGNAGE_TEAM_MEMBER: { text: " View Order", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      BROKERAGE_FIRM: { text: " Review Order in Portal", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      BROKERAGE_TEAM_MEMBER: { text: " Review Order in Portal", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
      AGENT: { text: " View My Orders", url: `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}` },
    },
    footer: "RealEsy. All rights reserved."
  },
};

